/**
 * On-screen Stores table column sizing: prioritize row content over header length.
 * Headers may wrap at word boundaries (see TableHead headerWrap); avoid overly narrow
 * min widths so titles are not forced to one character per line.
 */

export type StoresColumnSizing = {
  minWidth?: string;
  maxWidth?: string;
  width?: string;
  /** Allow header label to wrap to multiple lines (Stores table) */
  headerWrap?: boolean;
};

function isGeoField(col: {id: string; label: string}): boolean {
  const id = col.id;
  const lower = id.toLowerCase();
  if (lower === 'geo') {
    return true;
  }
  const parts = id.split(/\s*-\s*/).map(s => s.trim().toLowerCase());
  return parts.some(p => p === 'geo');
}

export function getStoresTableColumnSizing(col: {id: string; label: string}): StoresColumnSizing {
  const haystack = `${col.id} ${col.label}`.toLowerCase();
  const wrap: StoresColumnSizing = {headerWrap: true};

  /** Wide text columns */
  if (haystack.includes('email')) {
    return {
      ...wrap,
      minWidth: '14rem',
      maxWidth: 'min(52vw, 32rem)',
    };
  }
  if (haystack.includes('phonenumber') || haystack.includes('phone number')) {
    return {
      ...wrap,
      minWidth: '12rem',
      maxWidth: 'min(42vw, 22rem)',
    };
  }
  if (haystack.includes('storename') || haystack.includes('store name')) {
    return {
      ...wrap,
      minWidth: '15rem',
      maxWidth: 'min(52vw, 30rem)',
    };
  }

  if (haystack.includes('addressline') || haystack.includes('address line')) {
    return {
      ...wrap,
      minWidth: '10rem',
      maxWidth: 'min(42vw, 22rem)',
    };
  }

  /**
   * Compact columns: min widths large enough for at least one full word of the header
   * (no width: 1% — that shrinks columns until per-letter breaks occur).
   */
  if (haystack.includes('timezone') || haystack.includes('zone code')) {
    return {...wrap, minWidth: '8.5rem', maxWidth: '12rem'};
  }
  if (haystack.includes('siteid')) {
    return {...wrap, minWidth: '6.5rem', maxWidth: '9rem'};
  }
  if (haystack.includes('storenumber')) {
    return {...wrap, minWidth: '7rem', maxWidth: '10rem'};
  }
  if (haystack.includes('storeconcept') || haystack.includes('store concept')) {
    return {...wrap, minWidth: '7.5rem', maxWidth: '11rem'};
  }
  if (haystack.includes('brand')) {
    return {...wrap, minWidth: '6rem', maxWidth: '9rem'};
  }
  if (isGeoField(col)) {
    return {...wrap, minWidth: '3.5rem', maxWidth: '5.5rem'};
  }
  if (haystack.includes('region')) {
    return {...wrap, minWidth: '6.5rem', maxWidth: '10rem'};
  }
  if (haystack.includes('postal')) {
    return {...wrap, minWidth: '7rem', maxWidth: '10rem'};
  }
  if (/\bcountry\b/i.test(col.id) || /\bcountry\b/i.test(col.label)) {
    return {...wrap, minWidth: '8rem', maxWidth: '11rem'};
  }
  if (haystack.includes('city') || haystack.includes('state')) {
    return {...wrap, minWidth: '7.5rem', maxWidth: '11rem'};
  }
  if (haystack.includes('latitude') || haystack.includes('longitude')) {
    return {...wrap, minWidth: '7.5rem', maxWidth: '12rem'};
  }

  if (haystack.includes('address')) {
    return {
      ...wrap,
      minWidth: '10rem',
      maxWidth: 'min(40vw, 22rem)',
    };
  }

  return {
    ...wrap,
    minWidth: '6rem',
    maxWidth: 'min(28vw, 16rem)',
  };
}
